/**
 * CardErrorBoundary — region-scoped error boundary.
 *
 * The platform-wide `AppErrorBoundary` catches every render crash
 * but replaces the entire shell with a recovery surface. That is
 * the right behaviour for an unrecoverable crash, but it is far
 * too aggressive when one Instructor Dashboard card (say, the
 * Income summary card) throws while the other six cards are
 * perfectly healthy — the operator should never lose the rest of
 * the page because of one regressing region.
 *
 * `CardErrorBoundary` is the regional equivalent: it traps render
 * crashes inside the wrapped subtree, reports them via the same
 * diagnostic pipeline (`category=render_crash` + ref id), and
 * renders a calm slate placeholder in place of the broken region.
 * The rest of the dashboard continues to render.
 *
 * Doctrine pins:
 *   • No white-screen death states.
 *   • No silent emptiness — every async surface answers
 *     "is it loading / did it fail / what next".
 *   • Calm slate styling (no red panic).
 *
 * Usage:
 *
 *   <CardErrorBoundary region="income-summary">
 *     <IncomeSummaryCard />
 *   </CardErrorBoundary>
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

import {
  generateRefId,
  reportClientError,
} from "../lib/clientErrorReporter";

class CardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, refId: null };
    this._reset = this._reset.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true, refId: generateRefId() };
  }

  componentDidCatch(error, info) {
    reportClientError({
      refId: this.state.refId || undefined,
      category: "render_crash",
      component: this.props.region || "card-error-boundary",
      error,
      extras: {
        region: this.props.region || null,
        componentStack: info?.componentStack
          ? String(info.componentStack).slice(0, 2000)
          : null,
      },
    });
  }

  _reset() {
    this.setState({ hasError: false, refId: null });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const label = this.props.label || this.props.region || "this card";
    return (
      <div
        data-testid={`card-error-boundary-${this.props.region || "unknown"}`}
        data-region={this.props.region || undefined}
        className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/60 p-4"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              {label} didn't load
            </p>
            <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">
              The rest of your dashboard is still working — only this
              card had a problem.
            </p>
            <button
              data-testid={`card-error-boundary-retry-${this.props.region || "unknown"}`}
              onClick={this._reset}
              className="mt-2 text-xs font-bold text-slate-700 dark:text-slate-200 underline underline-offset-2"
            >
              Try this card again
            </button>
            {this.state.refId && (
              <p className="text-[10px] text-slate-400 mt-2 font-mono">
                Ref: {this.state.refId}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default CardErrorBoundary;
